
import { useAIAdvice } from "@/hooks/use-ai-advice";
import { AILoading } from "./ai-advice/AILoading";
import { AIEmpty } from "./ai-advice/AIEmpty";
import { AIContent } from "./ai-advice/AIContent";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Baby } from "lucide-react";

interface Child {
  id: string;
  name: string;
}

interface AIAdviceProps {
  caregiverId: string;
  children?: Child[];
  selectedChildId?: string;
  onChildSelect?: (childId: string) => void;
  compact?: boolean;
}

export function AIAdvice({ 
  caregiverId, 
  children,
  selectedChildId,
  onChildSelect,
  compact = false 
}: AIAdviceProps) {
  const { advice, loading, fetchAdvice } = useAIAdvice(caregiverId, selectedChildId);

  const handleChildChange = (childId: string) => {
    onChildSelect?.(childId);
  };

  // Show child selector if there are multiple children
  const showChildSelector = children && children.length > 1;
  const selectedChild = children?.find(c => c.id === selectedChildId);

  return (
    <div className={compact ? "p-4" : "p-6"}>
      {/* Child selector header */}
      {showChildSelector && (
        <div className="mb-4 pb-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <Baby className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium text-foreground">Analyzing:</span>
            <Select value={selectedChildId} onValueChange={handleChildChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select a child" />
              </SelectTrigger>
              <SelectContent>
                {children.map((child) => (
                  <SelectItem key={child.id} value={child.id}>
                    {child.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Single child display */}
      {children && children.length === 1 && (
        <div className="mb-4 pb-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <Baby className="h-5 w-5 text-primary" />
            <span className="text-sm text-foreground">
              Analyzing: <strong>{children[0].name}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && <AILoading compact={compact} />}

      {/* Empty state */}
      {!loading && !advice && <AIEmpty compact={compact} />}

      {/* Content */}
      {!loading && advice && <AIContent advice={advice} onRefresh={fetchAdvice} compact={compact} />}
    </div>
  );
}
